import React from "react";
import {
  ContentSection,
  Button,
  SectionHeading,
  Paragraph,
} from "@vwfs-bronson/bronson-react";
import { Formik } from "formik";
import { DocumentUploadSection } from "../Sections/DocumentUploadSection";
import { Output } from "../../Text";
import { isEuCitizen } from "../../common/countryCheck";
import { validateDocumentUpload } from "../Sections/DocumentUploadSection/validation";
import { UploadItemState } from "../../components/Polyfill";
import { SpinnerSection } from '../../components/Spinner';
import { Analytics } from '../../services/analytics';
import { setActualPage } from '../page.utils';
import { MAX_FILE_COUNT } from '../../components/DocumentUpload';

const PERSONAL_ID = "personalId";
const RESIDENCY_PERMIT = "residencyPermit";
const INCOME_PROOF = "incomeProof";

class DocumentUpload extends React.Component<
  DocumentUploadProps,
  DocumentUploadState
> {
  constructor(props: DocumentUploadProps) {
    super(props);

    this.state = {
      isUploading: false,
      showSpinner: false,
    };
    const { inputData } = props;
    this.livesInEu = isEuCitizen(inputData.nationality)
    this.props.onLoad();
  }

  livesInEu = false

  pageName = 'Document upload'

  defaultTrackingData = {
    [Analytics.keys.pageName()]: this.pageName,
    [Analytics.keys.viewChange()]: this.pageName,
  }

  trackPage = () => {
    setTimeout(() => {
      const pageData = {
        [Analytics.keys.eventType(0)]: 'pageView',
        [Analytics.keys.eventAction(0)]: 'Success',
      }
      Analytics.setTrackingData({...this.defaultTrackingData, ...pageData})
      Analytics.trackPage()
    }, 1000)
  }

  trackNonEu = () => {
    if (!this.livesInEu) {
      const euData = {
        [Analytics.keys.eventType(0)]: 'interAction',
        [Analytics.keys.eventAction(0)]: 'Success',
        [Analytics.keys.linkInformation(0)]: 'Non-EU customer',
      }
      Analytics.setTrackingData({...this.defaultTrackingData, ...euData})
      Analytics.trackInteraction()
    }
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    setActualPage(this.pageName)
    this.trackPage()
    this.trackNonEu()
  }

  getInitialValues = (documents: any) => {
    const personalIdArray: any[] = [];
    const residencyPermitArray: any[] = [];
    const incomeProofArray: any[] = [];

    const initialDocuments = {
      personalId: personalIdArray,
      residencyPermit: residencyPermitArray,
      incomeProof: incomeProofArray,
    };

    documents?.files?.filter((document: any) =>
      document.tags.includes(PERSONAL_ID)
    )?.forEach((personalIdDoc: any) => {
      initialDocuments.personalId.push(
        { ...personalIdDoc, state: UploadItemState.SUCCESS, size: personalIdDoc.fileSize }
      );
    })

    documents?.files?.filter((document: any) =>
      document.tags.includes(RESIDENCY_PERMIT)
    )?.forEach((residencyPermitDoc: any) => {
      initialDocuments.residencyPermit.push(
        { ...residencyPermitDoc, state: UploadItemState.SUCCESS, size: residencyPermitDoc.fileSize });
    })

    documents?.files?.filter((document: any) =>
      document.tags.includes(INCOME_PROOF)
    ).forEach((incomeProofDoc: any) => {
        initialDocuments.incomeProof.push(
          { ...incomeProofDoc, state: UploadItemState.SUCCESS, size: incomeProofDoc.fileSize }
        );
      })

    return initialDocuments;
  };

  enableSpinner() {
    this.setState({ showSpinner: true });
  }

  onSubmit() {
    if (this.state.isUploading){
      return;
    }

    window.setTimeout(this.enableSpinner.bind(this), 1000);
    this.setState({ isUploading: true });
    this.props.onSuccess();
  }

  render() {
    if (this.state.showSpinner) {
      return <SpinnerSection />;
    }

    const { transactionId, inputData, documents } = this.props;

    const livesInEu = isEuCitizen(inputData.nationality);

    return (
      <ContentSection pageWrap pageWrapSize="medium">
        <SectionHeading
          level="1"
          subtitle={Output.resolve("pages.heyCar.documentUpload.subtitle")}
        >
          {Output.resolve("pages.heyCar.documentUpload.title")}
        </SectionHeading>
        <Formik
          initialValues={this.getInitialValues(documents)}
          isInitialValid={(values: any) =>
            Object.keys(validateDocumentUpload(values.initialValues, livesInEu))
              .length === 0
          }
          validate={(values) => {
            return validateDocumentUpload(values, livesInEu);
          }}
          enableReinitialize={true}
          onSubmit={() => {
            this.onSubmit();
          }}
          render={(props: { isValid: boolean }) => {
            return (
              <>
                <DocumentUploadSection
                  fieldName={PERSONAL_ID}
                  subTitle={Output.resolve(
                    "pages.heyCar.documentUpload.personalId.subtitle"
                  )}
                  title={Output.resolve(
                    "pages.heyCar.documentUpload.personalId.title"
                  )}
                  transactionId={transactionId}
                  maxFiles={MAX_FILE_COUNT}
                />
                {!livesInEu && (
                  <DocumentUploadSection
                    fieldName={RESIDENCY_PERMIT}
                    subTitle={Output.resolve(
                      "pages.heyCar.documentUpload.residencyPermit.subtitle"
                    )}
                    title={Output.resolve(
                      "pages.heyCar.documentUpload.residencyPermit.title"
                    )}
                    transactionId={transactionId}
                    maxFiles={MAX_FILE_COUNT}
                  />
                )}
                <DocumentUploadSection
                  fieldName={INCOME_PROOF}
                  subTitle={Output.resolve(
                    "pages.heyCar.documentUpload.incomeProof.subtitle"
                  )}
                  title={Output.resolve(
                    "pages.heyCar.documentUpload.incomeProof.title"
                  )}
                  transactionId={transactionId}
                  maxFiles={MAX_FILE_COUNT}
                />
                <Paragraph className="u-text-right">
                  <Button
                    type="submit"
                    disabled={!props.isValid}
                    onClick={this.onSubmit.bind(this)}
                  >
                    {Output.resolve("pages.heyCar.documentUpload.cta")}
                  </Button>
                </Paragraph>
              </>
            );
          }}
        />
      </ContentSection>
    );
  }
}

type DocumentUploadProps = {
  transactionId: string;
  inputData: any;
  onLoad: () => Promise<void>
  onSuccess: () => Promise<void>;
  documents: any;
};

type DocumentUploadState = {
  isUploading: boolean;
  showSpinner: boolean;
};

export { DocumentUpload };
